import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toastHelper } from '../../Helpers/toastHelper';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEllipsisH,
  faThumbsUp,
  faSearch,
  faComment,
  faPaperPlane,
  faGlobeAmericas,
  faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faWhatsapp,
  faLinkedin
} from "@fortawesome/free-brands-svg-icons";
import ApiServices from "../Services/ApiServices";
import "../../assets/css/style.css";
import { getFullFileUrl } from "../../Helpers/UrlHelpers";
import { storageHelper } from "../../Helpers/storageHelper";
import BlogComment from "./BlogComment";
import MiniLoader from "../Shared/MiniLoader";
import htmlHelper from "../../Helpers/htmlHelper";
import Header from "../Shared/Header";
import platformLogo from "../../assets/images/new_log_design.svg";
import AdBanner from "../Shared/AdBanner";

const BlogAsPosts = () => {
  // Removed useParams as requested
  const navigate = useNavigate();

  // State for the main feed
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true); // Initial load
  const [loadingMore, setLoadingMore] = useState(false); // Infinite scroll load
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const observer = useRef();

  // Helper to format date like LinkedIn (e.g., "2d", "1w", or actual date)
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 1) return "اليوم";
    if (diffDays === 1) return "أمس";
    if (diffDays < 7) return `${diffDays} أيام`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} أسابيع`;
    return date.toLocaleDateString('ar-EG');
  };

  // Fetch Function
  const fetchInitialData = async (query) => {
    // If query is undefined, use activeSearchTerm (for reloads not changing search)
    // But be careful: 0 or empty string are valid.
    const termToUse = query !== undefined ? query : activeSearchTerm;

    setLoading(true);
    setHasMore(true);
    try {
      // Fetch the "Feed" using the new API endpoint
      const pageSize = 10;
      const feedResponse = await ApiServices.get(
        `Blog/getAllAsPosts?PageSize=${pageSize}&PageNumber=1&IsActive=true&Word=${termToUse}`
      );
      const feedPosts = feedResponse.data.items || [];
      setPosts(feedPosts);

      // Check if we have more pages
      const totalRecords = feedResponse.data.total_records;
      if (feedPosts.length >= totalRecords) {
        setHasMore(false);
      }
      setPage(1);
      setLoading(false);

    } catch (error) {

      setLoading(false);
    }
  };

  // Initial Data Fetch (Feed only)
  useEffect(() => {
    fetchInitialData("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setActiveSearchTerm(searchTerm);
    fetchInitialData(searchTerm);
  };

  // Load More Function
  const loadMorePosts = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const nextPage = page + 1;
    const pageSize = 10; // as per user request

    try {
      const response = await ApiServices.get(
        `Blog/getAllAsPosts?PageSize=${pageSize}&PageNumber=${nextPage}&IsActive=true&Word=${activeSearchTerm}`
      );
      const newPosts = response.data.items || [];

      // Filter out duplicates (checking against existing posts)
      setPosts(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const uniqueNewPosts = newPosts.filter(p => !existingIds.has(p.id));
        return [...prev, ...uniqueNewPosts];
      });

      // Check if we reached the end
      const totalRecords = response.data.total_records;
      if (newPosts.length < pageSize || (posts.length + newPosts.length) >= totalRecords) {
        setHasMore(false);
      }
      setPage(nextPage);
    } catch (error) {

    } finally {
      setLoadingMore(false);
    }
  };

  // Intersection Observer for Infinite Scroll
  const lastPostElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMorePosts();
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore]);


  // Handler to open post in viewer page
  const handlePostClick = (post) => {
    navigate(`/blog-viewer/${post.id}`);
  };

  // Component: LinkedIn Style Post Card (Moved outside to prevent re-renders on typing)
  const PostCard = ({ post, isLast, lastPostElementRef }) => {
    const viewRef = useRef(null);
    const hasViewed = useRef(false);
    const shareMenuRef = useRef(null);

    const [showComments, setShowComments] = useState(false);
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [likesCount, setLikesCount] = useState(post.likes || 0);
    const [isLiked, setIsLiked] = useState(post.is_liked || false);

    // Track view when post becomes visible
    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !hasViewed.current) {
            hasViewed.current = true;
            // Call View API
            ApiServices.put(`Blog/View/${post.id}`, {})
              .catch(() => { });
          }
        },
        { threshold: 0.5 } // 50% visibility
      );

      if (viewRef.current) {
        observer.observe(viewRef.current);
      }

      return () => {
        if (viewRef.current) {
          observer.unobserve(viewRef.current);
        }
      };
    }, [post.id]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
          setShowShareMenu(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);


    // Text with clickable links helper
    const formatContentWithLinks = (text) => {
      if (!text) return "";

      // Regex to find URLs (http, https, www)
      const urlRegex = /((?:https?:\/\/|www\.)[^\s]+)/g;

      const parts = text.split(urlRegex);

      return parts.map((part, index) => {
        if (part.match(urlRegex)) {
          let href = part;
          if (!part.startsWith('http')) {
            href = 'http://' + part;
          }
          // Remove trailing punctuation from URL if caught (common issue)
          // e.g. "google.com." should link to "google.com"
          const lastChar = href.slice(-1);
          if ([".", ",", "!", "?"].includes(lastChar)) {
            href = href.slice(0, -1);
            // We'll effectively just link the clean part, but for simplicity
            // in this split logic, the punctuation is part of the 'part' string.
            // A more robust regex handles this, but simply splitting is fine for display 
            // if we accept the punctuation is part of the link text or we carefully standardise.
            // For now, let's keep the href clean but display the full token as the link text or split it.
            // To keep it simple: strict split approach usually includes the dot in the link if no space.
          }

          return (
            <a
              key={index}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="post-link"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return part;
      });
    };



    // Smart truncation for description
    const getDescriptionText = () => {
      const text = htmlHelper.stripHtml(post.description || "");
      const shouldTruncate = text.length > 200;
      return shouldTruncate ? `${text.substring(0, 200)}...` : text;
    };

    const handleShare = (platform) => {
      const url = window.location.origin + `/blog-viewer/${post.id}`;
      const text = post.title || htmlHelper.stripHtml(post.description) || "تحقق من هذا المنشور";
      let shareUrl = "";

      switch (platform) {
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
          break;
        case 'whatsapp':
          shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text + "\n" + url)}`;
          break;
        case 'linkedin':
          shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
          break;
        default:
          return;
      }
      window.open(shareUrl, '_blank');
      setShowShareMenu(false);
    };

    const handleLike = async (e) => {
      e?.stopPropagation();
      const userId = storageHelper.getItem("user_id");
      if (!userId) {
        toastHelper.error("يجب عليك تسجيل الدخول اولا");
        return;
      }

      // Optimistic Update: Update UI immediately for instant feedback
      const previousLiked = isLiked;
      const previousLikesCount = likesCount;

      const newIsLiked = !isLiked;
      const newLikesCount = newIsLiked ? Number(likesCount) + 1 : Number(likesCount) - 1;

      setIsLiked(newIsLiked);
      setLikesCount(newLikesCount);

      try {
        const response = await ApiServices.put(`Blog/Like/${post.id}`, {});

        if (response.check || response.status === 200) {
          // Sync with actual count from API if available
          if (response.data && response.data.likes !== undefined) {
            setLikesCount(response.data.likes);
          }
        } else {
          // Revert on failure
          setIsLiked(previousLiked);
          setLikesCount(previousLikesCount);
        }
      } catch (error) {

        // Revert on error
        setIsLiked(previousLiked);
        setLikesCount(previousLikesCount);
      }
    };

    return (
      <div
        ref={(el) => {
          viewRef.current = el;
          if (isLast && lastPostElementRef) lastPostElementRef(el);
        }}
        className="linkedin-post-card"
        onClick={() => handlePostClick(post)}
      >
        {/* Header */}
        <div className="post-header">
          <div className="author-info">
            <div className="avatar-placeholder">
              {post.is_platform_blog ? (
                <img
                  src={platformLogo}
                  alt="منصة امتحاني"
                  className="platform-logo"
                />
              ) : post.author_image ? (
                <>
                  <img
                    src={getFullFileUrl(post.author_image)}
                    alt={post.author || "Author"}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="default-avatar-fallback" style={{ display: 'none', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                    <FontAwesomeIcon icon={faUser} />
                  </div>
                </>
              ) : (
                <FontAwesomeIcon icon={faUser} />
              )}
            </div>
            <div className="meta-text">
              <div className="author-name-wrapper">
                <h3 className="author-name">
                  {post.is_platform_blog ? "منصة امتحاني" : (post.author || "مستخدم مجهول")}
                </h3>
                {post.is_platform_blog && (
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="verification-badge"
                    title="حساب موثق"
                  />
                )}
              </div>
              {post.category_name && (
                <div className="post-category">{post.category_name}</div>
              )}
              <div className="sub-meta">
                <span className="post-date">{formatDate(post.added_date)}</span>
                <span className="dot-separator">•</span>
                <FontAwesomeIcon icon={faGlobeAmericas} className="privacy-icon" />
              </div>
            </div>
          </div>
          {/* <button className="post-options-btn">
          <FontAwesomeIcon icon={faEllipsisH} />
        </button> */}
        </div>

        {/* Content - Text/Html - Preview Only */}
        <div className="post-content">
          <p className="text-content" style={{ whiteSpace: 'pre-wrap' }}>
            {formatContentWithLinks(getDescriptionText())}
            {(htmlHelper.stripHtml(post.description || "").length > 200) && (
              <span className="see-more clickable">
                {" عرض المزيد"}
              </span>
            )}
          </p>
        </div>

        {/* Content - Image */}
        {post.image && (
          <div className="post-media">
            <img
              src={getFullFileUrl(post.image)}
              alt={post.title}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Stats / Engagement Bar */}
        <div className="post-stats">
          <div className="stats-left">
            <span className="stat-icon-group">
              <div className="icon-circle bg-blue">
                <FontAwesomeIcon icon={faThumbsUp} />
              </div>
            </span>
            <span className="stats-text">{likesCount} إعجاب</span>
            <span className="dot-separator">•</span>
            <span className="stats-text">{post.views || 0} مشاهدة</span>
          </div>
          <div className="stats-right">
            {/* Clickable comment count */}
            <span
              className="stats-text clickable"
              onClick={(e) => { e.stopPropagation(); setShowComments(!showComments); }}
            >
              {post.comments_count || post.comments || 0} تعليقات
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="post-actions" ref={shareMenuRef} onClick={(e) => e.stopPropagation()}>
          <button
            className={`action-button ${isLiked ? 'active' : ''}`}
            onClick={handleLike}
          >
            <FontAwesomeIcon icon={faThumbsUp} />
            <span>إعجاب</span>
          </button>

          <button
            className={`action-button ${showComments ? 'active' : ''}`}
            onClick={() => setShowComments(!showComments)}
          >
            <FontAwesomeIcon icon={faComment} />
            <span>تعليق</span>
          </button>

          {/* Share functionality combined into 'Send' button as user requested */}
          <div className="share-wrapper" style={{ flex: 1, position: 'relative' }}>
            <button
              className="action-button"
              onClick={() => setShowShareMenu(!showShareMenu)}
              style={{ width: '100%' }}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              <span>إرسال</span>
            </button>

            {showShareMenu && (
              <div className="share-popup">
                <button onClick={() => handleShare('facebook')} className="share-item">
                  <FontAwesomeIcon icon={faFacebook} style={{ color: '#1877F2' }} /> فيسبوك
                </button>
                <button onClick={() => handleShare('whatsapp')} className="share-item">
                  <FontAwesomeIcon icon={faWhatsapp} style={{ color: '#25D366' }} /> واتساب
                </button>
                <button onClick={() => handleShare('linkedin')} className="share-item">
                  <FontAwesomeIcon icon={faLinkedin} style={{ color: '#0A66C2' }} /> لينكد إن
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="post-comments-wrapper" onClick={(e) => e.stopPropagation()}>
            <BlogComment blogId={post.id} postOwnerId={post.user_id} />
          </div>
        )}
      </div>
    );
  };

  // Skeleton Loader Component (Moved outside)
  const PostSkeleton = () => (
    <div className="linkedin-post-card skeleton-card">
      <div className="post-header">
        <div className="skeleton-avatar shimmer"></div>
        <div className="skeleton-meta">
          <div className="skeleton-line title shimmer"></div>
          <div className="skeleton-line subtitle shimmer"></div>
        </div>
      </div>
      <div className="skeleton-body">
        <div className="skeleton-line text shimmer"></div>
        <div className="skeleton-line text shimmer"></div>
        <div className="skeleton-line text short shimmer"></div>
      </div>
      <div className="skeleton-media shimmer"></div>
    </div>
  );

  return (
    <>
      <Header />
      <AdBanner position="left" />

      <div className="linkedin-feed-container">
        <div className="feed-layout">

          {/* Main Feed Column */}
          <div className="feed-column">

            {/* Search Input */}
            <div className="search-box-container">
              <input
                type="text"
                placeholder="ابحث في الأخبار والمقالات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="search-input"
              />
              <button onClick={handleSearch} className="search-button">
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </div>

            {loading && posts.length === 0 ? (
              <>
                <PostSkeleton />
                <PostSkeleton />
              </>
            ) : posts.length > 0 ? (
              <>
                {posts.map((post, index) => (
                  <PostCard
                    key={`${post.id}-${index}`}
                    post={post}
                    isLast={index === posts.length - 1}
                    lastPostElementRef={lastPostElementRef}
                  />
                ))}
              </>
            ) : (
              <div className="no-results-message">
                <p>لا توجد نتائج تطابق بحثك</p>
              </div>
            )}

            {/* Bottom Loading State */}
            {loadingMore && (
              <div className="feed-loader">
                <MiniLoader size={24} color="#7a6ad8" variant="dots" />
              </div>
            )}

            {/* End of Feed Message */}
            {!hasMore && posts.length > 0 && (
              <div className="end-of-feed">
                <p>لا توجد منشورات أخرى للعرض</p>
                <div className="check-mark">✓</div>
              </div>
            )}

          </div>

        </div>
      </div>

      <style jsx>{`
        .linkedin-feed-container {
            background-color: #f3f2ef; /* LinkedIn bg color */
            min-height: 100vh;
            padding-top: 200px;
            padding-bottom: 40px;
            direction: rtl;
        }

        .feed-layout {
            max-width: 1128px;
            margin: 0 auto;
            display: flex;
            justify-content: center;
            gap: 24px;
            padding: 0 15px;
        }

        .feed-column {
            width: 100%;
            max-width: 650px; /* Typical feed width */
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .search-box-container {
            display: flex;
            align-items: center;
            background: white;
            border-radius: 8px;
            padding: 8px 16px;
            border: 1px solid #e0e0e0;
            box-shadow: 0 0 0 1px rgba(0,0,0,0.04), 0 2px 4px rgba(0,0,0,0.05);
            margin-bottom: 8px;
        }

        .search-input {
            flex: 1;
            border: none;
            outline: none;
            font-size: 14px;
            margin-left: 8px;
            text-align: right;
            font-family: inherit;
        }

        .search-button {
            border: none;
            background: none;
            color: #666;
            cursor: pointer;
            font-size: 16px;
            padding: 4px;
            transition: color 0.2s;
        }
        .search-button:hover {
            color: #7a6ad8;
        }

        /* Post Card Styles */
        .linkedin-post-card {
            background: white;
            border-radius: 8px;
            border: 1px solid #e0e0e0;
            box-shadow: 0 0 0 1px rgba(0,0,0,0.04), 0 2px 4px rgba(0,0,0,0.05);
            padding: 0;
            margin-bottom: 8px;
            position: relative; /* Create stacking context if needed */
            cursor: pointer;
            transition: box-shadow 0.2s, transform 0.1s;
        }

        .linkedin-post-card:hover {
            box-shadow: 0 0 0 1px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.1);
            transform: translateY(-2px);
        }

        .post-header {
            padding: 12px 16px 8px;
            display: flex;
            align-items: flex-start;
            gap: 12px;
            position: relative;
        }

        .author-info {
            display: flex;
            gap: 12px;
            flex: 1;
        }

        .avatar-placeholder {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            background-color: #e0e0e0;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            font-size: 20px;
            overflow: hidden;
            flex-shrink: 0;
        }

        .avatar-placeholder img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .platform-logo {
            object-fit: contain !important;
            padding: 4px;
        }

        .meta-text {
            display: flex;
            flex-direction: column;
            justify-content: center;
        }

        .author-name-wrapper {
            display: flex;
            align-items: center;
            gap: 6px;
        }

        .author-name {
            font-weight: 600;
            font-size: 14px;
            color: #191919;
            margin: 0;
            line-height: 1.4;
        }

        .verification-badge {
            color: #0a66c2;
            font-size: 14px;
            flex-shrink: 0;
        }

        .post-category {
            font-size: 12px;
            color: #666;
            margin-bottom: 2px;
        }

        .sub-meta {
            font-size: 12px;
            color: #666;
            display: flex;
            align-items: center;
            gap: 4px;
        }

        .dot-separator {
            font-weight: bold;
            margin: 0 2px;
        }

        .privacy-icon {
            font-size: 10px;
        }

        .post-options-btn {
            background: none;
            border: none;
            color: #666;
            cursor: pointer;
            padding: 4px 8px;
            font-size: 16px;
        }

        .post-content {
            padding: 4px 16px 8px;
            font-size: 14px;
            color: #191919;
            line-height: 1.5;
            white-space: pre-wrap;
        }
        
        .html-content {
            font-size: 14px;
            line-height: 1.6;
            overflow-wrap: break-word;
        }
        .html-content img {
            max-width: 100%;
            height: auto;
            margin: 10px 0;
        }

        .see-more {
            color: #666;
            cursor: pointer;
            font-weight: 600;
            margin-right: 4px;
        }
        .see-more:hover {
            color: #7a6ad8;
            text-decoration: underline;
        }
        
        .content-loader {
            display: flex;
            justify-content: center;
            padding: 20px;
        }

        .post-media {
            width: 100%;
            background-color: #f8f9fa; /* space filler */
            cursor: pointer;
            display: block;
        }

        .post-media img {
            width: 100%;
            height: auto;
            max-height: 600px;
            object-fit: contain; /* Like LinkedIn */
            display: block;
            background-color: #eef3f8;
        }

        .post-stats {
            padding: 10px 16px;
            border-bottom: 1px solid #e0e0e0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 12px;
            color: #666;
        }

        .stat-icon-group {
            display: flex;
            align-items: center;
            margin-left: 4px;
        }

        .icon-circle {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8px;
            color: white;
            margin-left: -4px; /* Stack effect */
            border: 1px solid white;
        }

        .bg-blue { background-color: #0a66c2; }
        .bg-red { background-color: #df704d; }

        .stats-right {
            display: flex;
            gap: 8px;
        }
        
        .clickable {
            cursor: pointer;
        }
        .clickable:hover {
            color: #0a66c2;
            text-decoration: underline;
        }

        .post-actions {
            display: flex;
            align-items: center;
            justify-content: space-between; /* Spread evenly */
            padding: 4px 8px;
        }

        .action-button {
            background: none;
            border: none;
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 8px;
            color: #666;
            font-weight: 600;
            font-size: 14px;
            cursor: pointer;
            border-radius: 4px;
            flex: 1; /* equal width */
            justify-content: center;
            transition: background 0.2s;
        }

        .action-button:hover {
            background-color: rgba(0,0,0,0.08); /* Light grey hover */
        }
        
        .action-button.active,
        .action-button.active svg,
        .action-button.active span {
            color: #0a66c2 !important; /* LinkedIn Blue */
            background-color: transparent;
        }

        .post-link {
            color: #0a66c2;
            text-decoration: none;
            font-weight: 500;
            cursor: pointer;
            direction: ltr; /* URLs look better LTR */
            unicode-bidi: embed;
        }

        .post-link:hover {
            text-decoration: underline;
            color: #004182;
        }
        
        /* Share Popup Styles */
        .share-popup {
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            background: white;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            padding: 8px 0;
            z-index: 10;
            min-width: 150px;
            margin-bottom: 8px;
        }
        
        .share-item {
            display: flex;
            align-items: center;
            gap: 12px;
            width: 100%;
            padding: 10px 16px;
            border: none;
            background: none;
            font-size: 14px;
            color: #333;
            cursor: pointer;
            text-align: right;
            transition: background 0.2s;
        }
        
        .share-item:hover {
            background-color: #f3f2ef;
        }
        
        /* Triangle indicator for popup optional */
        .share-popup::after {
            content: "";
            position: absolute;
            top: 100%;
            left: 50%;
            margin-left: -5px;
            border-width: 5px;
            border-style: solid;
            border-color: white transparent transparent transparent;
        }
        
        .post-comments-wrapper {
            border-top: 1px solid #eee;
            padding: 16px;
            background: #f9fafb;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
        }

        .post-header {
            padding: 12px 16px 8px;
            display: flex;
            align-items: flex-start;
            gap: 12px;
            position: relative;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
        }

        /* End of Feed */
        .end-of-feed {
            text-align: center;
            padding: 24px;
            color: #666;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
        }
        
        .check-mark {
            width: 32px;
            height: 32px;
            background: #d1fae5;
            color: #059669;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
        }
        
        .feed-loader {
            display: flex;
            justify-content: center;
            padding: 20px;
        }

        /* Skeleton Animation */
        .skeleton-card {
            padding: 16px;
            height: 300px;
        }
        
        .shimmer {
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
        }
        
        @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
        }
        
        .skeleton-avatar {
            width: 48px;
            height: 48px;
            border-radius: 50%;
        }
        
        .skeleton-meta {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 6px;
        }
        
        .skeleton-line {
            height: 10px;
            border-radius: 4px;
            background-color: #e0e0e0;
        }
        
        .skeleton-line.title { width: 120px; height: 14px; }
        .skeleton-line.subtitle { width: 80px; }
        
        .skeleton-body {
            margin-top: 16px;
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        
        .skeleton-line.text { width: 100%; }
        .skeleton-line.short { width: 60%; }
        
        .skeleton-media {
            margin-top: 16px;
            width: 100%;
            height: 180px;
            border-radius: 8px;
            background-color: #e0e0e0;
        }

        .no-results-message {
            text-align: center;
            padding: 40px 20px;
            background: white;
            border-radius: 8px;
            border: 1px solid #e0e0e0;
            color: #666;
            font-size: 16px;
            margin-bottom: 16px;
        }

        @media (max-width: 576px) {
            .feed-layout {
                padding: 0;
            }
            .linkedin-post-card {
                border-radius: 0; /* Full width on mobile often removes radius */
                border-left: none;
                border-right: none;
            }
        }
      `}</style>
    </>
  );
};

export default BlogAsPosts;
